import { Box, Button, Dialog, DialogActions, DialogContent, Grid, IconButton, Typography, useTheme } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { RootStateType } from "../../../redux-store/reducers";
import { accountNumberRegex, addressRegex, ifscRegex } from "../../../utils/regex";
import * as yup from 'yup';
import { DialogCloseIcon, InfoIconFilled } from "../../../assets/SvgIcons";
import { Form, Formik, useField, useFormikContext } from "formik";
import { MFSubmitButton, MFTextField } from "../../../lib/formik";
import { useRef, useState } from "react";
import { useSnackbar } from "notistack";
import { initialValuesJointHolder, updatedBank } from "../CreateJointHolder";
import { IfscType } from "../../../redux-store/types/mdms";
import { searchIFSCMaster } from "../../../redux-store/actions";
import { showError } from "../../../redux-store/actions/auth";
import { pennyDropVerification } from "../../../redux-store/actions/application";
import { Bank } from "../../../redux-store/types/api-types";
import { BankAccountTypeMaster } from "../../../utils/constant";
import UseRadioGroup from "../../../lib/formik/Radio";
import { LoadingButton } from "@mui/lab";

const initialBank: updatedBank = {
    ifscCode: '',
    bankAccountNumber: '',
    bankAccountType: 'Current',
    bankName: '',
    bankBranch: '',
    defaultBankAccount: false,
    isActive: true,
    isDefaultBank: false,
    pennydropCheck: false,
    pennydropVerified: false,
    holderName: '',
    address1: '',
    address2: '',
    address3: '',
    micrCode: '',
    bankAddress: '',
    loading: false,
    sno: Date.now(),
    pennydropResponse: '',
    // currency: '',
};
export const AddBankPopup = ({
    open,
    onClose,
    value,
    setFieldValue: setBankFieldValue,
    index,
    applicationId,
    primaryApplicantType
}: {
    open: boolean;
    onClose: () => void;
    value: any;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
    index?: number;
    applicationId: string;
    primaryApplicantType: string;
}): JSX.Element => {
    if (!open) return <></>;
    const theme = useTheme();
    const dispatch = useDispatch();
    const { currencyCode = "INR" } = useSelector((store: RootStateType) => store.currencyInfo);
    const [pennyDropLoading, setPennyDropLoading] = useState<boolean>(false);
    const [ifscLoading, setIfscLoading] = useState<boolean>(false);
    const [pennyDropErrMessage, setPennyDropErrMessage] = useState<string>('');
    const { enqueueSnackbar } = useSnackbar();
    const { bankDetails } = value as initialValuesJointHolder;
    let currentBank = {} as updatedBank;
    if (index !== -1) {
        currentBank = bankDetails[index as number]
    }
    const [fieldDisabled, setFieldDisabled] = useState<boolean>(true);

    function BankTextField({
        name,
        bankFieldName,
        branchFieldName,
        disabled = false,
        pennydropFieldCheck,
        address1FieldName,
        address2FieldName,
        address3FieldName,
        micrFieldName,
        addressFieldName,
        // currency
    }: {
        name: string;
        bankFieldName: string;
        branchFieldName: string;
        disabled?: boolean;
        pennydropFieldCheck: string;
        address1FieldName: string;
        address2FieldName: string;
        address3FieldName: string;
        micrFieldName: string;
        addressFieldName: string;
        // currency: string
    }): JSX.Element {
        const dispatch = useDispatch();
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const [_field, _meta, helpers] = useField(name);
        const [code, setCode] = useState<string>(_field.value);
        const { setFieldValue } = useFormikContext();
        const { setValue, setError } = helpers;
        const inputRef = useRef<HTMLInputElement>();

        const getIFSCResponse = (value: string) => {
            let isComponentAlive = true;
            if (!value || value.length < 12) {
                setValue((value || '').toUpperCase());
                setFieldValue(bankFieldName, '');
                setFieldValue(branchFieldName, '');
                setFieldValue(address1FieldName, '');
                setFieldValue(address2FieldName, '');
                setFieldValue(address3FieldName, '');
                setFieldValue(micrFieldName, '');
                setFieldValue(addressFieldName, '');
                // setFieldValue(currency, '');
            }
            if (value?.length !== 11) {
                return;
            }
            inputRef.current && inputRef.current.blur();
            (async function () {
                try {
                    setIfscLoading(true);
                    const { BankName, BankCity, BankAdd1, BankAdd2, BankAdd3, MicrNo } = (await dispatch(
                        searchIFSCMaster(value)
                    )) as unknown as IfscType;
                    if (!isComponentAlive) {
                        setIfscLoading(false);
                        return;
                    }
                    setFieldDisabled(true);
                    setFieldValue(bankFieldName, BankName);
                    setFieldValue(branchFieldName, BankCity);
                    setFieldValue(address1FieldName, BankAdd1);
                    setFieldValue(address2FieldName, BankAdd2);
                    setFieldValue(address3FieldName, BankAdd3);
                    setFieldValue(micrFieldName, MicrNo);
                    setFieldValue(
                        addressFieldName,
                        (BankAdd2 ? BankAdd1 + ',' : BankAdd1) + (BankAdd3 ? BankAdd2 + ',' : BankAdd2) + BankAdd3
                    );
                } catch (error) {
                    // setError('IFSC Code is invalid');
                    setFieldDisabled(false);
                } finally {
                    setIfscLoading(false);
                }
            })();
            return () => {
                isComponentAlive = false;
            };
        };
        function onChange({ target: { value } }: React.ChangeEvent<HTMLInputElement>) {
            setFieldDisabled(true);
            setFieldValue(pennydropFieldCheck, false);
            setCode(value);
            if (currencyCode?.toLowerCase() === "inr") {
                getIFSCResponse(value);
            } else {
                setValue((value || '').toUpperCase());
            }
        }
        return (
            <MFTextField
                name={name}
                label="IFSC Code *"
                placeholder="Enter IFSC Code"
                onChange={onChange}
                inputRef={inputRef}
                disabled={disabled}
                trimOnBlur={false}
            />
        );
    }

    const addBankSchema = yup.object().shape({
        ifscCode: yup.string().nullable().required('IFSC Code is required'),
        bankAccountNumber: yup.string().nullable().required('Account Number is required'),
        micrCode: yup.string().optional().nullable(),
        bankAccountType: yup.string().nullable().required('Bank Type is required'),
        // currency: yup.string().nullable().oneOf(Object.keys(CurrencyMaster), 'Invalid Bank Currency').required('Please Select Bank Currency'),
        bankName: yup.string().nullable().required('Bank Name is required'),
        bankBranch: yup.string().nullable().required('Bank Branch is required'),
        // bankAddress: yup.string().required('Bank Address is required'),
        address1: yup.string().nullable().matches(addressRegex, 'Invalid Bank Address'),
        address2: yup.string().nullable().matches(addressRegex, 'Invalid Bank Address'),
        address3: yup.string().nullable().matches(addressRegex, 'Invalid Bank Address'),
    });

    // const pennyDropCheck = (values: initialValuesJointHolder): boolean => {
    //   return values?.bankDetails
    //     .map((bank) => {
    //       if (bank?.loading) {
    //         return true;
    //       }
    //       return false;
    //     })
    //     .includes(true);
    // };

    const handleSubmit = (values: updatedBank, actions: any) => {
        const currentDetails = value?.bankDetails || [];
        if (!values.pennydropCheck) {
            dispatch(showError('Pennydrop check is required'));
        } else {
            if (index !== -1) {
                currentDetails[index as number] = values;
                setBankFieldValue(`bankDetails`, currentDetails);
            } else {
                setBankFieldValue(`bankDetails`, [...currentDetails, values]);
            }
            actions.resetForm();
            onClose();
        }

    }

    const InitialData = {
        ...initialBank,
        bankAccountType: primaryApplicantType === 'Individual' ?
            'CURRENT' : primaryApplicantType
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            sx={{
                '& .MuiPaper-root': {
                    borderRadius: '15px',
                    backgroundColor: 'background.default',
                    maxWidth: '900px',
                    padding: '25px',
                    m: 0
                }
            }}
        >
            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h2" sx={{ fontWeight: 600 }}>
                        Add Bank
                    </Typography>
                    <IconButton onClick={onClose}>
                        <DialogCloseIcon />
                    </IconButton>
                </Box>

                <Formik
                    initialValues={index !== -1 ? currentBank : InitialData}
                    validationSchema={addBankSchema}
                    // onSubmit={(values, actions) => {
                    //   const currentDetails = value?.bankDetails || [];
                    //   if (index !== -1) {
                    //     currentDetails[index as number] = values;
                    //     setBankFieldValue(`bankDetails`, currentDetails);
                    //   } else {
                    //     setBankFieldValue(`bankDetails`, [...currentDetails, values]);
                    //   }
                    //   actions.resetForm();
                    //   onClose();
                    // }}
                    onSubmit={handleSubmit}
                >
                    {({ values, setValues, setFieldValue }) => {
                        const handleBankVerification = async (values: updatedBank) => {
                            try {
                                setPennyDropLoading(true);
                                if (
                                    !values?.ifscCode ||
                                    !values?.bankAccountNumber ||
                                    (!values?.bankAccountType) ||
                                    (!values?.bankAccountType) ||
                                    !values?.bankName ||
                                    !values?.bankBranch
                                ) {
                                    throw `Please fill all the required(*) fields in Bank Account Details`;
                                }
                                if (currencyCode?.toLowerCase() === "inr" && !ifscRegex.test(values.ifscCode)) {
                                    throw `Invalid IFSC Code in Bank Account Details`;
                                }
                                if (currencyCode?.toLowerCase() === "inr" && !accountNumberRegex.test(values.bankAccountNumber)) {
                                    throw `Please enter valid Account Number in Bank Account Details`;
                                }

                                bankDetails?.map((bank, index) => {
                                    if (bank.isActive && bank.bankAccountNumber === values.bankAccountNumber && bank.sno !== values.sno) {
                                        throw `Account number already used`;
                                    }
                                });
                                const pennyDropResponse = (await dispatch(
                                    pennyDropVerification({
                                        ...values,
                                        applicationId: applicationId,
                                        modeOfHolding: value.modeOfHolding,

                                    })
                                )) as unknown as Bank;
                                if (!pennyDropResponse) {
                                    throw 'PennyDrop Verification Failed';
                                }
                                if (!pennyDropResponse?.pennydropVerified) {
                                    enqueueSnackbar('PennyDrop Verification Failed', {
                                        variant: 'error',
                                        autoHideDuration: 3000,
                                    });
                                };
                                setValues({
                                    ...values,
                                    ...pennyDropResponse
                                })
                                if (pennyDropResponse?.pennydropVerified) {
                                    enqueueSnackbar('PennyDrop Verification Successfully', {
                                        variant: 'success',
                                        autoHideDuration: 3000,
                                    });
                                }
                                if (!pennyDropResponse.pennydropVerified) {
                                    setPennyDropErrMessage(pennyDropResponse?.pennydropResponse || '');
                                }
                            } catch (e) {
                                setPennyDropLoading(false);
                                typeof e === 'string' && dispatch(showError(e));
                            } finally {
                                setPennyDropLoading(false);
                            }
                        };
                        return (
                            <Form>
                                <Grid container columnSpacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <BankTextField
                                            name={`ifscCode`}
                                            bankFieldName={`bankName`}
                                            branchFieldName={`bankBranch`}
                                            pennydropFieldCheck={`pennydropCheck`}
                                            address1FieldName={`address1`}
                                            address2FieldName={`address2`}
                                            address3FieldName={`address3`}
                                            micrFieldName={`micrCode`}
                                            addressFieldName={`bankAddress`}
                                        // currency={`currency`}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <MFTextField name="bankAccountNumber"
                                            label="Bank Account Number *"
                                            placeholder="Account Number"
                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                setFieldValue('pennydropCheck', false);
                                                setFieldValue('bankAccountNumber', event.target.value);
                                            }}
                                        />

                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <MFTextField
                                            name="micrCode"
                                            label="MICR Code"
                                            placeholder="MICR Code"
                                            // disabled={currencyCode?.toLowerCase() === "inr"}
                                            disabled={fieldDisabled}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} sx={{ display: { xs: 'none', sm: 'block' } }} />
                                    {/* <Grid item xs={12} sm={6}>
                    <MFSelectField
                      name={`currency`}
                      label="Bank Currency*"
                      items={Object.keys(CurrencyMaster).map((currency) => ({
                        key: CurrencyMaster[currency],
                        value: currency,
                      }))}

                      onChange={(event: SelectChangeEvent<unknown>) => {
                        const value = event.target.value as string;
                        setFieldValue('currency', value);
                        setFieldValue('pennydropCheck', false);
                      }}
                    />
                  </Grid> */}
                                    <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
                                        {`${primaryApplicantType ?? ''}`.toLowerCase() === "individual" ? (<UseRadioGroup
                                            formLabel="Bank Type *"
                                            name={`bankAccountType`}
                                            defaultValue="CURRENT"
                                            items={Object.keys(BankAccountTypeMaster).map((accountType) => ({
                                                label: accountType,
                                                value: BankAccountTypeMaster[accountType],
                                            }))}
                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                const value = event.target.value as string;
                                                setFieldValue('bankAccountType', value);
                                                setFieldValue('pennydropCheck', false);
                                            }}
                                            disabled={false}
                                        />) : < UseRadioGroup
                                            formLabel="Bank Type *"
                                            name={`bankAccountType`}
                                            items={['NRE', 'NRO'].map((primaryApplicantType) => ({
                                                label: primaryApplicantType,
                                                value: primaryApplicantType,
                                            }))}
                                            disabled={true}
                                        />}
                                    </Grid>
                                    <Grid item xs={12} sm={6} sx={{ display: { xs: 'none', sm: 'block' } }}>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <MFTextField
                                            name="bankName"
                                            label="Bank Name *"
                                            placeholder="Bank Name"
                                            // disabled={currencyCode?.toLowerCase() === "inr"}
                                            disabled={fieldDisabled}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <MFTextField
                                            name="bankBranch"
                                            label="Bank Branch *"
                                            placeholder="Bank Branch"
                                            // disabled={currencyCode?.toLowerCase() === "inr"}
                                            disabled={fieldDisabled}
                                        />
                                    </Grid>
                                    {/* <Grid item xs={12}>
                    <MFTextField
                      name={`bankAddress`}
                      multiline
                      rows={3}
                      label="Bank Address*"
                      placeholder="Enter Bank Address"
                      disabled={currencyCode?.toLowerCase() === "inr"}
                    />
                  </Grid> */}

                                    {/* <Grid item xs={12} sm={6}>
                    <MFTextField
                      name={`address1`}
                      label="Bank Address Line 1"
                      placeholder="Enter Bank Name"
                      // disabled={true} 
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const value = event.target.value as string;
                        setFieldValue('address1', value);
                        setFieldValue('pennydropCheck', false);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MFTextField
                      name={`address2`}
                      label="Bank Address Line 2"
                      placeholder="Enter Bank Name"
                      // disabled={true} 
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const value = event.target.value as string;
                        setFieldValue('address2', value);
                        setFieldValue('pennydropCheck', false);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MFTextField
                      name={`address3`}
                      label="Bank Address Line 3"
                      placeholder="Enter Bank Name"
                      // disabled={true} 
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const value = event.target.value as string;
                        setFieldValue('address3', value);
                        setFieldValue('pennydropCheck', false);
                      }}
                    />
                  </Grid> */}
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    mt={2}
                                    display="flex"
                                    justifyContent="space-between"
                                    sx={{
                                        width: '100%',
                                        flexDirection: { xs: 'column-reverse', sm: 'row' },
                                        alignItems: { xs: 'flex-start', sm: 'center' },
                                    }}>
                                    <Grid
                                        item
                                        xs={12}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: { xs: 'column', sm: 'row' },
                                            alignItems: 'center',
                                            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                                        }}>
                                        <LoadingButton
                                            loadingPosition="center"
                                            type="button"
                                            loading={pennyDropLoading}
                                            variant="outlined"
                                            sx={{
                                                color: 'primary.main',
                                                px: 3,
                                                fontWeight: 600,
                                                fontSize: theme.typography.body_400.fontSize,
                                                borderColor: 'primary.main',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                ':hover': {
                                                    color: 'common.white',
                                                    backgroundColor: 'primary.main',
                                                },
                                            }}
                                            onClick={() => {
                                                handleBankVerification(values);
                                            }}
                                            disabled={values.pennydropCheck || pennyDropLoading || ifscLoading}
                                        >
                                            {values.pennydropVerified || !values.pennydropCheck
                                                ? 'Verify Bank Details'
                                                : 'Verified'}
                                        </LoadingButton>
                                        <Typography
                                            component="div"
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                padding: { xs: 0, sm: '10px 20px' },
                                                borderRadius: '5px',
                                                alignItems: 'flex-start',
                                                mt: 2,
                                                width: '100%',
                                                boxSizing: 'border-box',
                                            }}>
                                            <InfoIconFilled width={24} height={24} />
                                            <Typography
                                                sx={{
                                                    color: `${theme.palette.text.secondary}`,
                                                    fontWeight: 400,
                                                    fontSize: theme.typography.body_400.fontSize,
                                                    ml: 1,
                                                }}>
                                                {values.pennydropCheck && !values.pennydropVerified
                                                    ? `${pennyDropErrMessage || ''}. Please upload your default bank account proofs if Default Bank`
                                                    : 'The Primary Investor’s bank account will be verified using a penny drop validation'}
                                            </Typography>
                                        </Typography>
                                    </Grid>
                                    {/* <Grid
                    item
                    sm={9}
                    xs={12}
                    sx={{ position: 'relative', bottom: 10, mb: { xs: 2, sm: 0 } }}>
                    <Typography
                      component="div"
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        padding: { xs: 0, sm: '10px 20px' },
                        borderRadius: '5px',
                        alignItems: 'flex-start',
                        mt: 2,
                        width: '100%',
                        boxSizing: 'border-box',
                      }}>
                      <InfoIconFilled width={24} height={24} />
                      <Typography
                        sx={{
                          color: `${theme.palette.text.secondary}`,
                          fontWeight: 400,
                          fontSize: theme.typography.body_400.fontSize,
                          ml: 1,
                        }}>
                        {values.pennydropCheck && !values.pennydropVerified
                          ? 'Penny drop verification failed, Unable to verify your bank details. Please upload your default bank account proofs if Default Bank'
                          : 'The Primary Investor’s bank account will be verified using a penny drop validation'}
                      </Typography>
                    </Typography>
                  </Grid> */}
                                </Grid>

                                <DialogActions sx={{ pt: 4 }}>
                                    <Grid item xs={12} sm={6}>
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            sx={{
                                                color: 'primary.500',
                                                backgroundColor: 'transparent',
                                                borderColor: 'primary.500',
                                                borderWidth: '0.5px',
                                                borderStyle: 'solid',
                                                padding: "10px",
                                                "&:hover, &.Mui-focusVisible": {
                                                    borderColor: 'primary.600',
                                                    backgroundColor: "background.default"
                                                },
                                                ...theme.typography.body_600
                                            }}
                                            onClick={onClose}
                                            disabled={pennyDropLoading || ifscLoading}
                                        >
                                            Cancel
                                        </Button>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Box mt={1} mb={1}>
                                            <MFSubmitButton label="Submit" disabled={pennyDropLoading || ifscLoading} loadingEnable={pennyDropLoading || ifscLoading} />
                                        </Box>
                                    </Grid>
                                </DialogActions>
                            </Form>
                        )
                    }}
                </Formik>
            </DialogContent>
        </Dialog >
    );
};